import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import patientsService, { Patient } from '../../services/patientService';

const Patients: React.FC = () => {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [filteredPatients, setFilteredPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<'name' | 'email'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const loadPatients = useCallback(async () => {
    try {
      setLoading(true);
      const response = await patientsService.getAll();
      const patientsData = Array.isArray(response) ? response : [];
      setPatients(patientsData);
      setFilteredPatients(patientsData);
    } catch (err: any) {
      console.error('Erro ao carregar pacientes:', err);
      Swal.fire({
        icon: 'error',
        title: 'Erro!',
        text: err.message || 'Erro ao carregar pacientes',
        confirmButtonColor: '#3085d6'
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPatients();
  }, [loadPatients]);

  // Filtrar e ordenar pacientes
  useEffect(() => {
    let result = [...patients];

    // Aplicar busca
    if (searchTerm) {
      result = result.filter(patient => {
        const fullName = patientsService.formatters.getFullName(patient).toLowerCase();
        const email = patientsService.formatters.getEmail(patient).toLowerCase();
        const search = searchTerm.toLowerCase();
        return fullName.includes(search) || email.includes(search);
      });
    }

    // Aplicar ordenação
    result.sort((a, b) => {
      let aValue: string;
      let bValue: string;

      if (sortField === 'name') {
        aValue = patientsService.formatters.getFullName(a).toLowerCase();
        bValue = patientsService.formatters.getFullName(b).toLowerCase();
      } else {
        aValue = patientsService.formatters.getEmail(a).toLowerCase();
        bValue = patientsService.formatters.getEmail(b).toLowerCase();
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredPatients(result);
    setCurrentPage(1); // Resetar para primeira página ao filtrar
  }, [patients, searchTerm, sortField, sortOrder]);

  const handleSort = (field: 'name' | 'email') => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const handleDeletePatient = async (id: string, name: string) => {
    const result = await Swal.fire({
      title: 'Tem certeza?',
      html: `
        <p>Você está prestes a excluir o paciente <strong>${name}</strong>.</p>
        <p class="text-danger"><i class="fas fa-exclamation-triangle"></i> <strong>Atenção:</strong> Todos os agendamentos deste paciente também serão excluídos permanentemente.</p>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sim, excluir tudo!',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        await patientsService.delete(id);
        
        Swal.fire({
          icon: 'success',
          title: 'Excluído!',
          text: 'Paciente e seus agendamentos foram excluídos com sucesso.',
          timer: 2000,
          showConfirmButton: false
        });
        
        await loadPatients();
      } catch (err: any) {
        console.error('Erro ao excluir paciente:', err);
        Swal.fire({
          icon: 'error',
          title: 'Erro!',
          text: err.message || 'Erro ao excluir paciente',
          confirmButtonColor: '#3085d6'
        });
      }
    }
  };

  const getSortIcon = (field: 'name' | 'email') => {
    if (sortField !== field) return <i className="fas fa-sort text-muted ml-1"></i>;
    return sortOrder === 'asc' 
      ? <i className="fas fa-sort-up text-primary ml-1"></i>
      : <i className="fas fa-sort-down text-primary ml-1"></i>;
  };

  // Paginação
  const totalPages = Math.ceil(filteredPatients.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPatients = filteredPatients.slice(startIndex, endIndex);

  const renderPagination = () => {
    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    if (startPage > 1) {
      pages.push(
        <li key="first" className="page-item">
          <button className="page-link" onClick={() => setCurrentPage(1)}>1</button>
        </li>
      );
      if (startPage > 2) {
        pages.push(<li key="dots1" className="page-item disabled"><span className="page-link">...</span></li>);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
          <button className="page-link" onClick={() => setCurrentPage(i)}>{i}</button>
        </li>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(<li key="dots2" className="page-item disabled"><span className="page-link">...</span></li>);
      }
      pages.push(
        <li key="last" className="page-item">
          <button className="page-link" onClick={() => setCurrentPage(totalPages)}>{totalPages}</button>
        </li>
      );
    }

    return (
      <ul className="pagination pagination-sm m-0">
        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
          <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1}>
            Anterior
          </button>
        </li>
        {pages}
        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
          <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages}>
            Próximo
          </button>
        </li>
      </ul>
    );
  };

  return (
    <>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1 className="m-0">Pacientes</h1>
            </div>
          </div>
        </div>
      </div>

      <section className="content">
        <div className="container-fluid">
          <div className="card">
            <div className="card-header">
              <div className="row align-items-center">
                <div className="col-md-6">
                  <h3 className="card-title mb-0">Lista de Pacientes</h3>
                </div>
                <div className="col-md-6 text-right">
                  <Link to="/patients/create" className="btn btn-sm btn-success">
                    <i className="fas fa-plus"></i> Novo Paciente
                  </Link>
                </div>
              </div>
              <div className="row mt-3">
                <div className="col-12">
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Buscar por nome ou e-mail..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <div className="input-group-append">
                      <span className="input-group-text">
                        <i className="fas fa-search"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="card-body p-0">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">Carregando...</span>
                  </div>
                  <p className="mt-2 text-muted">Carregando pacientes...</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped table-hover mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th style={{ cursor: 'pointer' }} onClick={() => handleSort('name')}>
                          Nome {getSortIcon('name')}
                        </th>
                        <th style={{ cursor: 'pointer' }} onClick={() => handleSort('email')}>
                          E-mail {getSortIcon('email')}
                        </th>
                        <th>Telefone</th>
                        <th>Data Nascimento</th>
                        <th className="text-center" style={{ width: '100px' }}>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentPatients.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="text-center py-4 text-muted">
                            <i className="fas fa-user-friends fa-2x mb-3 d-block"></i>
                            {searchTerm ? 'Nenhum paciente encontrado para sua busca' : 'Nenhum paciente cadastrado'}
                            {!searchTerm && (
                              <div className="mt-3">
                                <Link to="/patients/create" className="btn btn-success">
                                  <i className="fas fa-plus"></i> Cadastrar Primeiro Paciente
                                </Link>
                              </div>
                            )}
                          </td>
                        </tr>
                      ) : (
                        currentPatients.map((patient) => {
                          const fullName = patientsService.formatters.getFullName(patient);
                          const email = patientsService.formatters.getEmail(patient);
                          const phone = patientsService.formatters.getPhone(patient);
                          const birthDate = patientsService.formatters.formatDateBR(patient.birthDate);

                          return (
                            <tr key={patient.id}>
                              <td className="align-middle">
                                <strong>{fullName || '-'}</strong>
                              </td>
                              <td className="align-middle">
                                {email !== '-' ? <a href={`mailto:${email}`}>{email}</a> : '-'}
                              </td>
                              <td className="align-middle">{phone}</td>
                              <td className="align-middle">{birthDate}</td>
                              <td className="text-center align-middle">
                                <div className="btn-group btn-group-sm">
                                  <Link 
                                    to={`/patients/edit/${patient.id}`} 
                                    className="btn btn-info" 
                                    title="Editar"
                                  >
                                    <i className="fas fa-edit"></i>
                                  </Link>
                                  <button 
                                    className="btn btn-danger" 
                                    onClick={() => handleDeletePatient(patient.id, fullName)} 
                                    title="Excluir"
                                  >
                                    <i className="fas fa-trash"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            {!loading && filteredPatients.length > 0 && (
              <div className="card-footer clearfix">
                <div className="row align-items-center">
                  <div className="col-md-6">
                    <small className="text-muted">
                      Mostrando {currentPatients.length} de {filteredPatients.length} paciente(s)
                      {searchTerm && ` (filtrado de ${patients.length} total)`}
                    </small>
                  </div>
                  <div className="col-md-6">
                    <div className="float-right">
                      {renderPagination()}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default Patients;
